import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Paper, Grid, Alert,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { toJalaali } from 'jalaali-js';
import useFetch from '../../../../hooks/useFetch';
import SearchableAutocomplete from '../../../SearchableAutocomplete';
import StandardTable from '../../../common/table/StandardTable';
import StatusChip from '../../../common/StatusChip';
import { operationStatusTone } from '../../../common/statusTones';
import TablePagination from '../UserList/TablePagination';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { API } from '../../../../utils/apiRoutes';
import RTL from '../../../../utils/RTL';

const PERSIAN_MONTHS = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
];

const periodLabel = (value) => {
    if (!value) return '';
    const [y, m] = value.split('-').map(Number);
    return `${PERSIAN_MONTHS[(m || 1) - 1]} ${toPersianDigits(y)}`;
};

// Recent Jalali months (newest first) for the month picker — same pattern as
// the settlement form's settlement_period picker.
const buildRecentJalaliMonths = (count = 18) => {
    const now = new Date();
    const { jy, jm } = toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate());
    const out = [];
    let y = jy, m = jm;
    for (let i = 0; i < count; i++) {
        const value = `${y}-${String(m).padStart(2, '0')}`;
        out.push({ value, label: periodLabel(value) });
        m -= 1;
        if (m === 0) { m = 12; y -= 1; }
    }
    return out;
};
const RECENT_MONTHS = buildRecentJalaliMonths();

const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        return toPersianDigits(new Date(dateString).toLocaleDateString('fa-IR'));
    } catch {
        return '-';
    }
};

const weekdayOf = (dateString) => {
    if (!dateString) return '';
    try {
        return new Date(dateString).toLocaleDateString('fa-IR', { weekday: 'long' });
    } catch {
        return '';
    }
};

export default function TeacherAttendanceReview() {
    const [teacherId, setTeacherId] = useState(null);
    const [period, setPeriod] = useState(RECENT_MONTHS[0].value);
    const [courseFilter, setCourseFilter] = useState('');

    const params = useMemo(
        () => ({ teacher_id: teacherId, year_month: period }),
        [teacherId, period]
    );
    const { data, loading, error } = useFetch(API.teacherAttendanceLog, {
        params,
        skip: !teacherId || !period,
    });

    const allRows = useMemo(() => data?.results || [], [data]);
    const courseNames = useMemo(
        () => [...new Set(allRows.map((r) => r.course_name).filter(Boolean))],
        [allRows]
    );
    const rows = useMemo(
        () => (courseFilter ? allRows.filter((r) => r.course_name === courseFilter) : allRows),
        [allRows, courseFilter]
    );

    // Reset the course filter when the teacher/month changes.
    useEffect(() => { setCourseFilter(''); }, [teacherId, period]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    useEffect(() => { setPage(0); }, [rows]);
    const pagedRows = useMemo(
        () => rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
        [rows, page, rowsPerPage]
    );

    const columns = useMemo(() => [
        {
            id: 'date_belonged',
            label: 'تاریخ\nجلسه',
            render: (row) => (
                <Typography variant="body2">
                    {formatDate(row.date_belonged)}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                        {weekdayOf(row.date_belonged)}
                    </Typography>
                </Typography>
            ),
        },
        {
            id: 'operation_date',
            label: 'تاریخ\nثبت',
            render: (row) => (
                <Typography variant="body2">{formatDate(row.operation_date)}</Typography>
            ),
        },
        {
            id: 'student_name',
            label: 'هنرجو',
            align: 'start',
            render: (row) => (
                <Typography variant="body2">{row.student_name || '-'}</Typography>
            ),
        },
        {
            id: 'course_name',
            label: 'دوره',
            align: 'start',
            render: (row) => (
                <Typography variant="body2">{row.course_name || '-'}</Typography>
            ),
        },
        {
            id: 'status',
            label: 'نوع\nعملیات',
            render: (row) => (
                <StatusChip label={row.status || '-'} tone={operationStatusTone(row.status)} />
            ),
        },
        {
            id: 'comment',
            label: 'توضیحات',
            align: 'start',
            render: (row) => (
                <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.comment || '-'}
                </Typography>
            ),
        },
        {
            id: 'staff',
            label: 'ثبت‌کننده',
            align: 'start',
            render: (row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">{row.staff || '-'}</Typography>
                    {row.created_via === 'assistant' && (
                        <StatusChip tone="info" variant="outlined" label="دستیار هوشمند" />
                    )}
                </Box>
            ),
        },
    ], []);

    return (
        <RTL>
        <Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
                بازبینی حضور و غیاب
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                همه عملیات‌های ثبت‌شده برای دوره‌های یک آموزگار در یک ماه — برای تطبیق با لیست کاغذی حضور و غیاب.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid
                    container
                    spacing={2}
                    sx={{ '& .MuiTextField-root': { m: 0 }, '& .MuiFormControl-root': { m: 0 } }}
                >
                    <Grid item xs={12} sm={4}>
                        <SearchableAutocomplete
                            apiUrl="/accounts/api/v1/profile/teachers/"
                            getOptionLabel={(option) =>
                                `${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim() || option.user?.email || ''
                            }
                            getOptionValue={(option) => option?.user?.id}
                            value={teacherId}
                            onChange={(value) => setTeacherId(value || null)}
                            label="آموزگار"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>ماه</InputLabel>
                            <Select
                                value={period}
                                label="ماه"
                                onChange={(e) => setPeriod(e.target.value)}
                            >
                                {RECENT_MONTHS.map((m) => (
                                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small" disabled={courseNames.length === 0}>
                            <InputLabel>دوره</InputLabel>
                            <Select
                                value={courseFilter}
                                label="دوره"
                                onChange={(e) => setCourseFilter(e.target.value)}
                            >
                                <MenuItem value="">همه دوره‌ها</MenuItem>
                                {courseNames.map((name) => (
                                    <MenuItem key={name} value={name}>{name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {!teacherId ? (
                <Alert severity="info">برای نمایش فهرست، آموزگار و ماه را انتخاب کنید.</Alert>
            ) : (
                <>
                    {!loading && !error && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {`تعداد ردیف‌ها: ${toPersianDigits(rows.length)}`}
                        </Typography>
                    )}
                    <StandardTable
                        columns={columns}
                        rows={pagedRows}
                        loading={loading}
                        error={error}
                        emptyText="در این ماه عملیاتی برای این آموزگار ثبت نشده است"
                        getRowKey={(r) => r.id}
                        ariaLabel="teacher-attendance-log"
                        minWidth={750}
                        stickyHeader
                    />
                    {rows.length > rowsPerPage && (
                        <Paper variant="outlined" sx={{ mt: 1 }}>
                            <TablePagination
                                totalCount={rows.length}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                onPageChange={(newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                responsive
                            />
                        </Paper>
                    )}
                </>
            )}
        </Box>
        </RTL>
    );
}
